import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { toast } from 'sonner';

type Announcement = Awaited<ReturnType<typeof api.operator.announcements.list>>[number];

export function AnnouncementList() {
  const navigate = useNavigate();
  const [list, setList] = useState<Announcement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [linkHref, setLinkHref] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const data = await api.operator.announcements.list();
      setList(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler');
    }
  }

  useEffect(() => { load(); }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error('Titel und Text sind Pflicht');
      return;
    }
    setSubmitting(true);
    try {
      await api.operator.announcements.create({
        title: title.trim(),
        body: body.trim(),
        linkHref: linkHref.trim() || undefined,
        linkLabel: linkLabel.trim() || undefined,
      });
      toast.success('Ankündigung veröffentlicht');
      setTitle('');
      setBody('');
      setLinkHref('');
      setLinkLabel('');
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Ankündigung "${title}" wirklich löschen?`)) return;
    try {
      await api.operator.announcements.delete(id);
      toast.success('Gelöscht');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    }
  }

  return (
    <AdminShell pageTitle="Announcements">
      <PageHeader title="Ankündigungen" showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">

        {!showForm && (
          <Button onClick={() => setShowForm(true)}>Neue Ankündigung</Button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-border px-4 py-4">
            <label className="block space-y-1">
              <span className="text-sm font-medium">Titel</span>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="z.B. Neu: Personalisierung im Entdeckungs-Chat"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Text</span>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Erkläre kurz, was sich geändert hat und wie Nutzer davon profitieren."
                rows={5}
                className="text-sm resize-none"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block space-y-1">
                <span className="text-sm font-medium">Link-Ziel (optional)</span>
                <Input
                  value={linkHref}
                  onChange={(e) => setLinkHref(e.target.value)}
                  placeholder="/einstellungen"
                />
              </label>
              <label className="block space-y-1">
                <span className="text-sm font-medium">Link-Text (optional)</span>
                <Input
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="Jetzt entdecken"
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Veröffentliche…' : 'Veröffentlichen'}
              </Button>
            </div>
          </form>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        {list && list.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground">Noch keine Ankündigungen.</p>
        )}

        {list?.map((a) => (
          <div key={a.id} className="rounded-xl border border-border px-4 py-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{a.title}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(a.publishedAt).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(a.id, a.title)}>
                Löschen
              </Button>
            </div>
            <p className="text-sm text-foreground/80 whitespace-pre-line">{a.body}</p>
            {a.linkHref && a.linkLabel && (
              <a href={a.linkHref} className="text-xs text-primary underline underline-offset-2">
                {a.linkLabel}
              </a>
            )}
          </div>
        ))}

        <Button variant="outline" onClick={() => navigate(routes.coach.operator.dashboard())}>
          Zurück
        </Button>
      </div>
    </AdminShell>
  );
}
